import { AsyncButton, Button } from '@components/button';
import { Form, FormTextInput } from '@components/form';
import { FormTagsInput } from '@components/form/FormInputs/FormTagsInput';
import { useCreateChannelMutation, useGetOrganizationSettingsQuery } from '@generated/graphql';
import { Stack, Typography } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import posthog from 'posthog-js';
import * as yup from 'yup';
import { VolumesWithAccessDropdown } from './VolumesWithAccessDropdown';

type NewChannelModalProps = {
  onClose: () => void;
  refetch: () => void;
  organizationId: string;
};

type SubmitType = {
  name: string;
  description: string;
  timeout: number;
  volumes: string[];
  tags: string[];
};

const validationSchema = yup.object({
  name: yup
    .string()
    .trim()
    .max(128, 'Must be less than 128 characters')
    .required('Name is required'),
  description: yup.string(),
  timeout: yup
    .number()
    .positive('Timeout must be a positive number')
    .required('Timeout is required'),
});

export const NewChannelModal = ({ onClose, organizationId, refetch }: NewChannelModalProps) => {
  const { useAsyncNotification } = useNotifications();
  const [createChannel] = useCreateChannelMutation();
  const { refetch: channelsLimitsRefetch } = useGetOrganizationSettingsQuery({
    variables: { organizationId },
  });

  const handleFormSubmit = useAsyncNotification(
    'Successfully created the new channel',
    async ({ name, description, volumes, timeout, tags }: SubmitType) => {
      const { data } = await createChannel({
        variables: { name, organizationId, description, volumes, timeout: Number(timeout), tags },
      });
      posthog.capture('user_create-channel_submitted');
      if (data) {
        await channelsLimitsRefetch();
        refetch();
        onClose();
      }
    },
  );

  return (
    <Form
      enableReinitialize
      initialValues={{
        name: '',
        description: '',
        volumes: [],
        timeout: '',
        tags: [],
      }}
      validationSchema={validationSchema}
      validateOnBlur={false}
      onSubmit={handleFormSubmit}
    >
      {({ isValid, handleSubmit, dirty, isSubmitting }) => (
        <Stack>
          <FormTextInput name="name" label="Name" />
          <FormTextInput name="description" label="Description" multiline rows={3} />
          <VolumesWithAccessDropdown
            name="volumes"
            label="Volumes with access"
            organizationId={organizationId}
          />
          <FormTextInput name="timeout" type="number" label="Timeout (secs)" />
          <FormTagsInput name="tags" />
          <Stack gap={4}>
            <AsyncButton
              fullWidth
              loading={isSubmitting}
              disabled={!(isValid && dirty && !isSubmitting)}
              onClick={handleSubmit}
            >
              Create
            </AsyncButton>
            <Button fullWidth variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      )}
    </Form>
  );
};

export const NewChannelModalHelpContent = (
  <Typography variant="body2">
    A Channel is a container for Graphs, Packages (sensors and application specific requirements)
    and code that is used to define the universe of possible synthetic output for a particular
    application. For example, Channels may represent synthetic data generation as diverse as video
    of microscopy or satellite based SAR data acquisition. All of the components of a Channel
    together define the set of capabilities that solve a specific synthetic generation use-case. The
    code for a channel is packaged as a docker image and uploaded to the cloud via the
    &apos;anadeploy&apos; command line tool.
  </Typography>
);
